"use client";
import PrimaryBtn from "@/components/PrimaryBtn";
import Loader from "@/components/Loader";
import { Icons } from "@assets/assets";
import { getAllJobList } from "@/lib/ApiClient";
import { useEffect, useState } from "react";
import { JobItem } from "@/lib/Types";

interface OpeningsProps {
  baseURL: string;
}

export default function Openings({ baseURL }: OpeningsProps) {
  const [jobArray, setJobArray] = useState<JobItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [jobArrayAtt, setJobArrayAtt] = useState({
    currentPage: 1,
    totalPage: 1,
    itemCount: 1,
    totalItems: 1,
  });

  useEffect(() => {
    async function fetchJobDetails() {
      try {
        setLoading(true);
        const limit = 3;
        const data = await getAllJobList(jobArrayAtt.currentPage, limit);
        if (data) {
          setJobArray((prev) =>
            jobArrayAtt.currentPage === 1
              ? data.items || []
              : [...prev, ...(data.items || [])],
          );
        }
        setJobArrayAtt((prev) => ({
          ...prev,
          currentPage: data.currentPage,
          totalPage: data.totalPages,
          itemCount: data.itemCount,
          totalItems: data.totalItems,
        }));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchJobDetails();
  }, [jobArrayAtt.currentPage]);

  const handleLoadMore = () => {
    try {
      setJobArrayAtt((prev) => ({
        ...prev,
        currentPage: prev.currentPage + 1,
      }));
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.log(err.message);
      } else {
        console.log("An unexpected error occurred");
      }
    }
  };

  return (
    <section
      className="py-[30px] xl:py-[50px] relative z-2 bg-[#D9E2ED]"
      id="career-form"
    >
      <div className="max-w-[2500px] flex-1 mx-auto w-[calc(100%_-_10%)] flex flex-col relative z-10">
        <div className="career-header border-b border-white pb-[20px] justify-between flex flex-col lg:flex-row items-center gap-[15px] xl:gap-[60px] w-full lg:w-auto">
          <div className="career-header-left w-full lg:w-auto leading-[110%]">
            <p className="self-stretch justify-center text-[#636363] text-base font-medium uppercase tracking-tight pb-4">
              Current Openings
            </p>
            <h2 className="self-stretch flex lg:flex-col justify-start text-[length:var(--section-title)] font-medium clash-font">
              <span className="text-[#00040b]  capitalize leading-[110%]">
                Current Job
              </span>
              &nbsp;
              <span className="text-[#225bd4]  capitalize leading-[110%]">
                Vacancies
              </span>
            </h2>
            <h2 className="self-stretch justify-start text-[length:var(--section-title)] font-medium clash-font">
              <span className="text-[#00040b]  capitalize leading-[110%]"></span>
            </h2>
          </div>
          <div className="career-header-right">
            <div className="w-full justify-start max-w-[965px] text-[#00040b]/70 text-[length:var(--hero-para)] font-normal leading-[150%]">
              We consider each team member as a part of our family. Our work
              culture is rooted in transparency, meaningful collaboration, and
              inclusivity. Explore career opportunities and find the role that
              fits your skills.
              <br />
            </div>
          </div>
        </div>
        <div className="career-container pt-[40px] flex">
          <div className="career-area flex flex-col xl:flex-row justify-between w-full">
            <div className="career-box text-[length:var(--hero-para)] xl:max-w-[20ch] italic">
              <p>
                Can’t find your dream job role? Send us your resume to&nbsp;
                <a
                  href="mailto:hr@konstantinfo.com"
                  className="text-[#225BD4] font-semibold "
                >
                  hr@konstantinfo.com
                </a>
              </p>
            </div>
            <div className="career-list flex flex-col gap-[30px] max-w-[965px] flex-1">
              {loading && jobArray?.length === 0 ? (
                <div className="flex justify-center items-center w-full py-10 min-h-[400px]">
                  <Loader size={80} />
                </div>
              ) : jobArray?.length > 0 ? (
                jobArray?.map((items, index) => (
                  <div
                    key={index}
                    className="career-box flex flex-col xl:flex-row gap-4 xl:gap-[30px] xl:items-center justify-between py-4 border-b border-[#fff]"
                  >
                    <div className="career-left">
                      <h3 className="text-xl md:text-[length:var(--career-list-title)] font-[500] clash-font text-[#00040B]">
                        {items?.job_title}
                      </h3>
                      <ul className="flex flex-wrap gap-2 items-center">
                        <li className="text-[#636364] text-sm md:text-[length:var(--primarybtn)]">
                          Language: {items?.language || "0 Experiences"}
                        </li>
                        <div className="mt-2 xl:mt-0 dot w-1 h-1 rounded-full bg-[#636364] shrink-0 mb-1 md:mb-0"></div>
                        <li className="text-[#636364] text-sm md:text-[length:var(--primarybtn)]">
                          Experience: {items?.experience}
                        </li>
                        <div className="mt-2 xl:mt-0 dot w-1 h-1 rounded-full bg-[#636364] shrink-0 mb-1 md:mb-0"></div>
                        <li className="text-[#636364] text-sm md:text-[length:var(--primarybtn)]">
                          Location: {items?.location}
                        </li>
                      </ul>
                    </div>
                    <PrimaryBtn
                      dark={false}
                      text="Apply Now"
                      iconSrc={Icons?.chevron}
                      href={`${baseURL}/${items?.slug}`}
                    />
                  </div>
                ))
              ) : (
                <p>No Jobs</p>
              )}

              {jobArray?.length > 0 &&
                jobArrayAtt?.currentPage < jobArrayAtt.totalPage && (
                  <div className="text-center md:text-left pt-[10px]">
                    <PrimaryBtn
                      dark={false}
                      text="View More Jobs"
                      iconSrc={Icons?.chevron}
                      onClick={handleLoadMore}
                    />
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
